import { useAuth } from '../providers/auth-provider';
import { useOrders, useProjects, useTrainees, useProductionLogs } from '@t90/api-client';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Package,
  Users,
  FileText,
  CheckCircle,
  Clock,
  AlertTriangle,
} from '@t90/ui';
import { formatCurrency, formatNumber } from '@t90/common';
import { PROJECT_STATUSES } from '@t90/common';

export function DashboardPage() {
  const { user } = useAuth();
  const { data: orders, isLoading: ordersLoading } = useOrders({ limit: 5 });
  const { data: projects, isLoading: projectsLoading } = useProjects({ status: PROJECT_STATUSES.IN_PROGRESS, limit: 5 });
  const { data: trainees, isLoading: traineesLoading } = useTrainees({ status: 'Active', limit: 10 });
  // TODO: Replace with correct assignmentId string when available
  const { data: productionLogs, isLoading: productionLoading } = useProductionLogs(undefined);

  // Calculate metrics
  const activeOrders = orders?.filter((o) => o.order_status !== 'Completed' && o.order_status !== 'Cancelled').length ?? 0;
  const activeProjects = projects?.length ?? 0;
  const activeTrainees = trainees?.length ?? 0;
  const todaysUnits = productionLogs?.reduce((sum, log) => sum + (log.units_completed ?? 0), 0) ?? 0;

  const greeting = getGreeting();

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          {greeting}, {user?.full_name?.split(' ')[0]}!
        </h1>
        <p className="text-muted-foreground">
          Here's what's happening at Turn90 today.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Active Orders"
          value={formatNumber(activeOrders)}
          description="Orders in progress"
          icon={FileText}
          loading={ordersLoading}
        />
        <StatCard
          title="Active Projects"
          value={formatNumber(activeProjects)}
          description="Projects being worked on"
          icon={Package}
          loading={projectsLoading}
        />
        <StatCard
          title="Active Trainees"
          value={formatNumber(activeTrainees)}
          description="Trainees on roster"
          icon={Users}
          loading={traineesLoading}
        />
        <StatCard
          title="Today's Units"
          value={formatNumber(todaysUnits)}
          description="Units completed today"
          icon={CheckCircle}
          loading={productionLoading}
        />
      </div>

      {/* Content Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Orders */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Orders</CardTitle>
            <CardDescription>Latest customer orders</CardDescription>
          </CardHeader>
          <CardContent>
            {ordersLoading ? (
              <LoadingSkeleton />
            ) : orders?.length ? (
              <div className="space-y-4">
                {orders.slice(0, 5).map((order) => (
                  <div
                    key={order.id}
                    className="flex items-center justify-between"
                  >
                    <div>
                      <p className="font-medium">{order.order_number}</p>
                      <p className="text-sm text-muted-foreground">
                        {order.customer?.customer_name ?? 'Unknown Customer'}
                      </p>
                    </div>
                    <Badge variant={getOrderStatusVariant(order.order_status)}>
                      {order.order_status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState message="No orders found" />
            )}
          </CardContent>
        </Card>

        {/* Active Projects */}
        <Card>
          <CardHeader>
            <CardTitle>Active Projects</CardTitle>
            <CardDescription>Projects currently in progress</CardDescription>
          </CardHeader>
          <CardContent>
            {projectsLoading ? (
              <LoadingSkeleton />
            ) : projects?.length ? (
              <div className="space-y-4">
                {projects.slice(0, 5).map((project) => (
                  <div
                    key={project.id}
                    className="flex items-center justify-between"
                  >
                    <div>
                      <p className="font-medium">{project.project_number}</p>
                      <p className="text-sm text-muted-foreground">
                        {(project as any).order?.customer?.customer_name ?? 'Unknown Customer'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">
                        {formatNumber(project.units_completed ?? 0)} / {formatNumber(project.quantity ?? 0)}
                      </p>
                      <p className="text-xs text-muted-foreground">units</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState message="No active projects" />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Helper Components
interface StatCardProps {
  title: string;
  value: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  loading?: boolean;
}

function StatCard({ title, value, description, icon: Icon, loading }: StatCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-8 w-24 animate-pulse rounded bg-muted" />
        ) : (
          <>
            <div className="text-2xl font-bold">{value}</div>
            <p className="text-xs text-muted-foreground">{description}</p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-4 w-32 animate-pulse rounded bg-muted" />
            <div className="h-3 w-24 animate-pulse rounded bg-muted" />
          </div>
          <div className="h-6 w-20 animate-pulse rounded bg-muted" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <div className="rounded-full bg-muted p-3">
        <Package className="h-6 w-6 text-muted-foreground" />
      </div>
      <p className="mt-2 text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function getOrderStatusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' {
  switch (status) {
    case 'New':
      return 'secondary';
    case 'In Progress':
      return 'default';
    case 'Completed':
      return 'success';
    case 'Cancelled':
      return 'destructive';
    case 'On Hold':
      return 'warning';
    default:
      return 'secondary';
  }
}
